import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import apiClient from '../services/api';

/**
 * Web Vitals monitoring utility
 * Tracks Core Web Vitals and sends them to the analytics backend
 *
 * Metrics tracked:
 * - CLS (Cumulative Layout Shift) - Visual stability
 * - INP (Interaction to Next Paint) - Responsiveness
 * - FCP (First Contentful Paint) - Loading performance
 * - LCP (Largest Contentful Paint) - Loading performance
 * - TTFB (Time to First Byte) - Server response time
 */

interface WebVitalsPayload {
  name: string;
  value: number;
  rating: string;
  delta: number;
  id: string;
  navigationType: string;
  url: string;
  userAgent: string;
  timestamp: string;
}

// Track if vitals have been initialized to prevent duplicate sends
let initialized = false;

// Buffer to collect metrics before sending (debounced send)
const metricsBuffer: WebVitalsPayload[] = [];
let sendTimer: ReturnType<typeof setTimeout> | null = null;
let lastSentAt = 0;
const DEFAULT_MIN_SEND_INTERVAL_MS = 60_000;
const minSendIntervalMs = (() => {
  const envOverride = import.meta.env.VITE_WEB_VITALS_MIN_INTERVAL_MS;
  if (envOverride) {
    const parsed = Number(envOverride);
    if (!Number.isNaN(parsed) && parsed >= 1000) {
      return parsed;
    }
  }
  return DEFAULT_MIN_SEND_INTERVAL_MS;
})();

/**
 * Send collected metrics to analytics endpoint
 */
async function sendMetrics() {
  if (metricsBuffer.length === 0) return;

  const now = Date.now();
  if (now - lastSentAt < minSendIntervalMs) {
    // Reschedule send for later to avoid flooding the endpoint
    scheduleSend();
    return;
  }

  const metrics = [...metricsBuffer];
  metricsBuffer.length = 0; // Clear buffer
  lastSentAt = now;

  try {
    await apiClient.api.post('/v1/analytics/web-vitals', {
      metrics,
      sessionId: getSessionId(),
      deviceType: getDeviceType()
    });
  } catch (error) {
    console.error('[Web Vitals] Failed to send metrics:', error);
    // Don't throw - vitals tracking should never break the app
  }
}

/**
 * Schedule debounced send of metrics (wait 5s after last metric)
 */
function scheduleSend() {
  if (sendTimer) {
    clearTimeout(sendTimer);
  }
  sendTimer = setTimeout(sendMetrics, 5000);
}

/**
 * Get or create session ID for correlating metrics
 */
function getSessionId(): string {
  const key = 'vitly-session-id';
  try {
    let sessionId = sessionStorage.getItem(key);
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem(key, sessionId);
    }
    return sessionId;
  } catch (_error) {
    // iOS Safari private mode - generate temporary ID
    return `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }
}

/**
 * Detect device type for segmented analysis
 */
function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Process metric and add to buffer
 */
function handleMetric(metric: Metric) {
  const payload: WebVitalsPayload = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    url: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };

  metricsBuffer.push(payload);
  scheduleSend();

  // Also log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta
    });
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when the app starts
 */
export function initWebVitals() {
  if (initialized) {
    console.warn('[Web Vitals] Already initialized');
    return;
  }

  // Only track in production or if explicitly enabled
  const shouldTrack = import.meta.env.PROD || import.meta.env.VITE_TRACK_WEB_VITALS === 'true';

  if (!shouldTrack) {
    console.log('[Web Vitals] Tracking disabled (not in production)');
    return;
  }

  initialized = true;

  try {
    // Track Core Web Vitals
    onCLS(handleMetric);
    onINP(handleMetric); // Responsiveness (replaced FID)
    onFCP(handleMetric);
    onLCP(handleMetric);
    onTTFB(handleMetric);

    console.log('[Web Vitals] Monitoring initialized');

    // Send any buffered metrics before page unload
    window.addEventListener('beforeunload', () => {
      if (sendTimer) {
        clearTimeout(sendTimer);
      }
      sendMetrics();
    });

    // Also send on visibility change (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (sendTimer) {
          clearTimeout(sendTimer);
        }
        sendMetrics();
      }
    });
  } catch (error) {
    console.error('[Web Vitals] Failed to initialize:', error);
  }
}

/**
 * Manually send any buffered metrics
 * Useful for testing or forcing a send
 */
export function flushWebVitals() {
  if (sendTimer) {
    clearTimeout(sendTimer);
    sendTimer = null;
  }
  return sendMetrics();
}
